import React, { useState } from 'react'

/* ─────────────────────────────────────────────────────────────
   Member Profile
   Per brief:
   - Full name (passport-exact, name change triggers admin alert)
   - Email, phone, country — all editable
   - Profile picture — editable
   - Member ID — visible, NOT editable
   - Professional details: institution type, name, job title,
     position, country they represent
   - Saved travel companions
   - Member ID displayed, not editable
───────────────────────────────────────────────────────────── */

const INSTITUTION_TYPES = [
  'Embassy',
  'Consulate General',
  'Consulate',
  'Permanent Mission',
  'International Organization',
  'Private Office',
  'Corporate C-Suite',
  'Government Ministry',
  'Royal Household',
  'Other',
]

const ALL_COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Argentina','Armenia','Australia',
  'Austria','Azerbaijan','Bahrain','Bangladesh','Belarus','Belgium','Belize','Benin',
  'Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria',
  'Burkina Faso','Burundi','Cambodia','Cameroon','Canada','Cape Verde','Chad','Chile',
  'China','Colombia','Comoros','Congo','Costa Rica','Croatia','Cuba','Cyprus',
  'Czech Republic','Denmark','Djibouti','Dominican Republic','Ecuador','Egypt',
  'El Salvador','Eritrea','Estonia','Ethiopia','Fiji','Finland','France','Gabon',
  'Gambia','Georgia','Germany','Ghana','Greece','Guatemala','Guinea','Haiti','Honduras',
  'Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kuwait','Kyrgyzstan','Laos','Latvia',
  'Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg',
  'Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Mauritania','Mauritius',
  'Mexico','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar',
  'Namibia','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','Norway',
  'Oman','Pakistan','Palestine','Panama','Paraguay','Peru','Philippines','Poland',
  'Portugal','Qatar','Romania','Russia','Rwanda','Saudi Arabia','Senegal','Serbia',
  'Sierra Leone','Singapore','Slovakia','Slovenia','Somalia','South Africa','South Korea',
  'Spain','Sri Lanka','Sudan','Sweden','Switzerland','Syria','Taiwan','Tajikistan',
  'Tanzania','Thailand','Togo','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay',
  'Uzbekistan','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe',
]

/* ── Saved companion entry ── */
function CompanionCard({ companion, index, onUpdate, onRemove }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="cnt-companion">
      <div className="cnt-companion__header">
        <div className="cnt-companion__avatar">{companion.initials || '?'}</div>
        <div className="cnt-companion__info">
          <span className="cnt-companion__name">{companion.name || `Companion ${index + 1}`}</span>
          <span className="cnt-companion__role">{companion.role || 'Unspecified'}</span>
        </div>
        <div className="cnt-companion__actions">
          <button type="button" className="cnt-icon-btn" onClick={() => setOpen(o => !o)} aria-label="Edit companion">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M9.5 1.5l2 2L4 11H2v-2L9.5 1.5Z" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <button type="button" className="cnt-icon-btn cnt-icon-btn--danger" onClick={() => onRemove(index)} aria-label="Remove companion">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 2l9 9M11 2L2 11" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round"/></svg>
          </button>
        </div>
      </div>
      {open && (
        <div className="cnt-companion__form">
          <div className="cnt-row-2">
            <div className="cnt-field">
              <label className="cnt-label">Full Name</label>
              <input className="cnt-input" value={companion.name} onChange={e => onUpdate(index, 'name', e.target.value)} placeholder="As on passport" />
            </div>
            <div className="cnt-field">
              <label className="cnt-label">Relationship</label>
              <select className="cnt-select" value={companion.role} onChange={e => onUpdate(index, 'role', e.target.value)}>
                <option value="">Select…</option>
                <option>Spouse</option>
                <option>Assistant</option>
                <option>Colleague</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          <div className="cnt-row-2">
            <div className="cnt-field">
              <label className="cnt-label">Passport Number</label>
              <input className="cnt-input" value={companion.passport} onChange={e => onUpdate(index, 'passport', e.target.value)} placeholder="Passport number" />
            </div>
            <div className="cnt-field">
              <label className="cnt-label">Nationality</label>
              <select className="cnt-select" value={companion.nationality} onChange={e => onUpdate(index, 'nationality', e.target.value)}>
                <option value="">Select…</option>
                {ALL_COUNTRIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
export default function MemberProfile({ onBack, member }) {
  const [saved,        setSaved]        = useState(false)
  const [nameChanged,  setNameChanged]  = useState(false)

  /* Personal */
  const [fullName,     setFullName]     = useState(member.name)
  const [email,        setEmail]        = useState(member.email       || '')
  const [phone,        setPhone]        = useState(member.phone       || '')
  const [country,      setCountry]      = useState(member.country     || '')
  const [avatar,       setAvatar]       = useState(null)

  /* Professional */
  const [instType,     setInstType]     = useState(member.institution || '')
  const [instOther,    setInstOther]    = useState('')
  const [instName,     setInstName]     = useState('')
  const [jobTitle,     setJobTitle]     = useState(member.jobTitle    || '')
  const [position,     setPosition]     = useState(member.position    || '')
  const [represents,   setRepresents]   = useState(member.represents  || '')

  /* Companions */
  const [companions,   setCompanions]   = useState([
    { name: 'Marie Al-Rashidi', role: 'Spouse',    initials: 'MA', passport: '', nationality: '' },
  ])

  const handleNameChange = (val) => {
    setFullName(val)
    if (val !== member.name) setNameChanged(true)
    else setNameChanged(false)
  }

  const handleSave = (e) => {
    e.preventDefault()
    /* In production: POST to Laravel API */
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const addCompanion = () => {
    setCompanions(prev => [...prev, { name: '', role: '', initials: '', passport: '', nationality: '' }])
  }

  const updateCompanion = (i, field, val) => {
    setCompanions(prev => prev.map((c, idx) => {
      if (idx !== i) return c
      const updated = { ...c, [field]: val }
      if (field === 'name') {
        updated.initials = val.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
      }
      return updated
    }))
  }

  const removeCompanion = (i) => {
    setCompanions(prev => prev.filter((_, idx) => idx !== i))
  }

  return (
    <div className="db-page cnt-page">

      {/* Header */}
      <div className="cnt-header">
        <button className="cnt-back" onClick={onBack} aria-label="Back">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Dashboard
        </button>
        <div className="cnt-header__title-wrap">
          <p className="cnt-header__overline">Member</p>
          <h1 className="cnt-header__title">My Profile</h1>
        </div>
      </div>

      <form className="cnt-form" onSubmit={handleSave} noValidate>

        {/* ── Avatar + Member ID ── */}
        <div className="cnt-section">
          <div className="cnt-profile-top">
            <div className="cnt-avatar-wrap">
              <div className="cnt-avatar">
                {avatar
                  ? <img src={URL.createObjectURL(avatar)} alt="Profile" className="cnt-avatar__img" />
                  : <span className="cnt-avatar__initials">{member.initials}</span>
                }
              </div>
              <label className="cnt-avatar-btn" aria-label="Change profile photo">
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files[0] && setAvatar(e.target.files[0])} />
                Change photo
              </label>
            </div>
            <div className="cnt-member-id-block">
              <span className="cnt-label">Member ID</span>
              <span className="cnt-member-id">{member.memberId}</span>
              <span className="cnt-member-id-note">Controlled by Mwuïlu. Not editable.</span>
              <span className="cnt-member-since">Member since {member.joinDate || 'January 2024'}</span>
            </div>
          </div>
        </div>

        {/* ── Personal details ── */}
        <div className="cnt-section">
          <p className="cnt-section__label">Personal Details</p>

          <div className="cnt-field">
            <label className="cnt-label" htmlFor="fullName">Full Name</label>
            <input
              id="fullName"
              className="cnt-input"
              value={fullName}
              onChange={e => handleNameChange(e.target.value)}
              placeholder="Exactly as on your passport"
            />
            <span className="cnt-field-hint">Your name must match your passport exactly.</span>
            {nameChanged && (
              <span className="cnt-field-alert">
                A name change will be noted and sent to the Mwuïlu team for awareness.
              </span>
            )}
          </div>

          <div className="cnt-row-2">
            <div className="cnt-field">
              <label className="cnt-label" htmlFor="email">Email Address</label>
              <input id="email" type="email" className="cnt-input" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="cnt-field">
              <label className="cnt-label" htmlFor="phone">Phone Number</label>
              <input id="phone" type="tel" className="cnt-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+32 2 xxx xx xx" />
            </div>
          </div>

          <div className="cnt-field">
            <label className="cnt-label" htmlFor="country">Country of Residence</label>
            <select id="country" className="cnt-select" value={country} onChange={e => setCountry(e.target.value)}>
              <option value="">Select country…</option>
              {ALL_COUNTRIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* ── Professional details ── */}
        <div className="cnt-section">
          <p className="cnt-section__label">Professional Details</p>

          <div className="cnt-field">
            <label className="cnt-label" htmlFor="instType">Institution Type</label>
            <select id="instType" className="cnt-select" value={instType} onChange={e => setInstType(e.target.value)}>
              <option value="">Select…</option>
              {INSTITUTION_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          {instType === 'Other' && (
            <div className="cnt-field">
              <label className="cnt-label" htmlFor="instOther">Institution Type (specify)</label>
              <input id="instOther" className="cnt-input" value={instOther} onChange={e => setInstOther(e.target.value)} />
            </div>
          )}

          <div className="cnt-field">
            <label className="cnt-label" htmlFor="instName">Institution Name</label>
            <input id="instName" className="cnt-input" value={instName} onChange={e => setInstName(e.target.value)} placeholder="Embassy of the UAE, Acme Group…" />
          </div>

          <div className="cnt-row-2">
            <div className="cnt-field">
              <label className="cnt-label" htmlFor="jobTitle">Job Title</label>
              <input id="jobTitle" className="cnt-input" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="Ambassador, CEO, Managing Director…" />
            </div>
            <div className="cnt-field">
              <label className="cnt-label" htmlFor="position">Position</label>
              <input id="position" className="cnt-input" value={position} onChange={e => setPosition(e.target.value)} placeholder="Head of Mission, Chief of Staff…" />
            </div>
          </div>

          <div className="cnt-field">
            <label className="cnt-label" htmlFor="represents">Country Represented / Operates Under</label>
            <select id="represents" className="cnt-select" value={represents} onChange={e => setRepresents(e.target.value)}>
              <option value="">Select country…</option>
              {ALL_COUNTRIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* ── Saved travel companions ── */}
        <div className="cnt-section">
          <div className="cnt-section-header">
            <p className="cnt-section__label">Saved Travel Companions</p>
            <button type="button" className="cnt-add-btn" onClick={addCompanion}>
              + Add companion
            </button>
          </div>
          <p className="cnt-section-note">
            Companions saved here can be selected instantly when adding passengers to any request.
          </p>

          {companions.length === 0 && (
            <p className="cnt-empty">No saved companions yet.</p>
          )}

          <div className="cnt-companions-list">
            {companions.map((comp, i) => (
              <CompanionCard
                key={i}
                companion={comp}
                index={i}
                onUpdate={updateCompanion}
                onRemove={removeCompanion}
              />
            ))}
          </div>
        </div>

        {/* ── Save ── */}
        <div className="cnt-form__footer">
          {saved && (
            <span className="cnt-save-confirm">Profile updated successfully.</span>
          )}
          <button type="submit" className="cnt-btn-primary cnt-btn-primary--wide">
            Save Changes
          </button>
        </div>

      </form>
    </div>
  )
}